// src/pages/user/CSBaoHanhDoiTra.tsx
import React, { useEffect } from 'react';
import styled, { ThemeProvider, DefaultTheme } from 'styled-components';
import { useTranslation, Trans } from 'react-i18next';

// --- THEME, TYPES, VÀ STYLED COMPONENTS (Giữ nguyên) ---
const theme = {
  colors: {
    background: '#f8f9fa',
    surface: '#ffffff',
    primary: '#38a169',
    text: '#4a5568',
    heading: '#1a202c',
    subtleText: '#718096',
  },
  fonts: {
    body: "'Inter', sans-serif",
  },
  fontSizes: {
    base: '1rem',
    h1: '2.5rem',
    h2: '1.8rem',
    h3: '1.3rem',
  },
  spacing: {
    sm: '8px',
    md: '16px',
    lg: '24px',
    xl: '32px',
    xxl: '48px',
  },
  shadows: {
    main: '0 8px 30px rgba(0, 0, 0, 0.08)',
  },
  borderRadius: '12px',
  breakpoints: {
    tablet: '768px',
  },
};

type AppTheme = typeof theme;

declare module 'styled-components' {
  export interface DefaultTheme extends AppTheme {}
}

const PageContainer = styled.div`
  background-color: ${({ theme }) => theme.colors.background};
  padding: ${({ theme }) => theme.spacing.xxl} 15px;
  font-family: ${({ theme }) => theme.fonts.body};
  min-height: 100vh;
`;

const ContentCard = styled.div`
  max-width: 850px;
  margin: 0 auto;
  background-color: ${({ theme }) => theme.colors.surface};
  border-radius: ${({ theme }) => theme.borderRadius};
  padding: ${({ theme }) => theme.spacing.xxl};
  box-shadow: ${({ theme }) => theme.shadows.main};
  color: ${({ theme }) => theme.colors.text};
  line-height: 1.8;

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    padding: ${({ theme }) => theme.spacing.xl};
  }
`;

const Heading = styled.h2`
  margin-top: ${({ theme }) => theme.spacing.xxl};
  margin-bottom: ${({ theme }) => theme.spacing.lg};
  color: ${({ theme }) => theme.colors.heading};
  font-size: ${({ theme }) => theme.fontSizes.h2};
  font-weight: 700;
  border-bottom: 2px solid ${({ theme }) => theme.colors.primary};
  padding-bottom: ${({ theme }) => theme.spacing.md};
  line-height: 1.4;

  &:first-of-type {
    margin-top: 0;
  }
`;

const SubHeading = styled.h3`
  margin-top: ${({ theme }) => theme.spacing.xl};
  margin-bottom: ${({ theme }) => theme.spacing.md};
  color: ${({ theme }) => theme.colors.heading};
  font-size: ${({ theme }) => theme.fontSizes.h3};
  font-weight: 700;
  line-height: 1.5;
`;

const Paragraph = styled.p`
  margin-bottom: ${({ theme }) => theme.spacing.md};
  font-size: ${({ theme }) => theme.fontSizes.base};
  text-align: justify;
`;

const List = styled.ul`
  list-style: none;
  padding-left: 0;
  margin-bottom: ${({ theme }) => theme.spacing.lg};
`;

const ListItem = styled.li`
  margin-bottom: ${({ theme }) => theme.spacing.md};
  display: flex;
  align-items: flex-start;
  font-size: ${({ theme }) => theme.fontSizes.base};
  text-align: justify;

  &::before {
    content: '•';
    color: ${({ theme }) => theme.colors.primary};
    font-weight: 700;
    margin-right: 12px;
    font-size: 1.2em;
    line-height: 1.4;
  }
`;

const InfoBlock = styled.div`
  background-color: ${({ theme }) => theme.colors.background};
  border-left: 4px solid ${({ theme }) => theme.colors.primary};
  padding: ${({ theme }) => theme.spacing.md} ${({ theme }) => theme.spacing.lg};
  margin: ${({ theme }) => theme.spacing.lg} 0;
  border-radius: 0 ${({ theme }) => theme.borderRadius} ${({ theme }) => theme.borderRadius} 0;
`;

const StyledLink = styled.a`
  color: ${({ theme }) => theme.colors.primary};
  font-weight: 600;
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
`;

// --- COMPONENT CHÍNH ---
const CSBaoHanhDoiTra: React.FC = () => {
  const { t } = useTranslation();

  useEffect(() => {
    document.title = t('warrantyReturnPage.helmetTitle');
  }, [t]);

  return (
    <ThemeProvider theme={theme}>
      <PageContainer>
        <ContentCard>
          <Heading id="doi-tra">{t('warrantyReturnPage.returnPolicy.heading')}</Heading>
          <Paragraph>{t('warrantyReturnPage.returnPolicy.p1')}</Paragraph>
          <List>
            <ListItem>{t('warrantyReturnPage.returnPolicy.li1')}</ListItem>
            <ListItem>{t('warrantyReturnPage.returnPolicy.li2')}</ListItem>
            <ListItem>{t('warrantyReturnPage.returnPolicy.li3')}</ListItem>
          </List>
          <InfoBlock>
            <Paragraph>
              <Trans
                i18nKey="warrantyReturnPage.returnPolicy.infoBlock.p1"
                components={{ strong: <strong /> }}
              />
            </Paragraph>
            <Paragraph>
              <Trans
                i18nKey="warrantyReturnPage.returnPolicy.infoBlock.p2"
                components={{
                  link1: <StyledLink href="tel:0867003331" />,
                  link2: <StyledLink href="tel:0867003337" />,
                }}
              />
            </Paragraph>
          </InfoBlock>

          <Heading id="bao-hanh">{t('warrantyReturnPage.warrantyPolicy.heading')}</Heading>
          <Paragraph>
            <Trans
              i18nKey="warrantyReturnPage.warrantyPolicy.p1"
              components={{ strong: <strong /> }}
            />
          </Paragraph>
          
          <SubHeading>{t('warrantyReturnPage.warrantyPolicy.conditions.heading')}</SubHeading>
          <Paragraph>{t('warrantyReturnPage.warrantyPolicy.conditions.p1')}</Paragraph>
          <List>
            <ListItem>{t('warrantyReturnPage.warrantyPolicy.conditions.li1')}</ListItem>
            <ListItem>{t('warrantyReturnPage.warrantyPolicy.conditions.li2')}</ListItem>
          </List>

          <SubHeading>{t('warrantyReturnPage.warrantyPolicy.rejections.heading')}</SubHeading>
          <Paragraph>{t('warrantyReturnPage.warrantyPolicy.rejections.p1')}</Paragraph>
          <List>
            <ListItem>{t('warrantyReturnPage.warrantyPolicy.rejections.li1')}</ListItem>
            <ListItem>{t('warrantyReturnPage.warrantyPolicy.rejections.li2')}</ListItem>
            <ListItem>{t('warrantyReturnPage.warrantyPolicy.rejections.li3')}</ListItem>
            <ListItem>{t('warrantyReturnPage.warrantyPolicy.rejections.li4')}</ListItem>
            <ListItem>{t('warrantyReturnPage.warrantyPolicy.rejections.li5')}</ListItem>
          </List>
        </ContentCard>
      </PageContainer>
    </ThemeProvider>
  );
};

export default CSBaoHanhDoiTra;